import { Reference, useApolloClient } from "@apollo/client";
import { useSnackbar } from "notistack";
import React from "react";
import {
  GenerateQuotePdfInput,
  QuoteDocumentSourceInput,
} from "../../../../clients/graphqlTypes.js";
import { useGenerateQuoteDocumentPdfMutation } from "../../quote-documents/QuoteDocuments.generated.js";
import { assertNever } from "../../../utils.js";

export type PdfDownloadArgs =
  | { templateType: "none"; calculationType: "agreed" | "proposed" }
  | {
      templateType: "contracting";
      calculationType: "agreed" | "proposed";
      contractingPdfId: string;
    }
  | {
      templateType: "sexy";
      calculationType: "proposed";
      pdfTemplateUrl: string;
    }
  | {
      templateType: "generator";
      calculationType: "agreed" | "proposed";
      pdfGeneratorTemplateId: string;
    };

export function useDownloadQuotePdf({
  projectId,
  docId,
}: {
  projectId: string;
  docId: string;
}) {
  const [loadingPdf, setLoadingPdf] = React.useState<null | string>(null);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [generateQuotePdf] = useGenerateQuoteDocumentPdfMutation({ client });

  const generate = async (
    args: PdfDownloadArgs,
    save: GenerateQuotePdfInput["save"],
    pdfLetterheadId?: string | null
  ) => {
    if (loadingPdf) return;

    try {
      setLoadingPdf(getLoadingKey(args));
      const { data } = await generateQuotePdf({
        variables: {
          input: {
            projectId,
            quoteId: docId,
            source: toSourceInput(args),
            save: save ?? null,
            // Omitted rather than null: an explicit null fails coercion against
            // the previous schema, which the parallel deploy still serves.
            ...(pdfLetterheadId ? { pdfLetterheadId } : {}),
          },
        },
        // The list read goes to a replica, so the created document is written
        // into the cache from the mutation's own result (design D13).
        update: (cache, { data }) => {
          const quoteDocument = data?.generateQuotePdf.quoteDocument;
          if (!quoteDocument) return;
          cache.modify<{ documents: Reference[] }>({
            id: cache.identify({ __typename: "Quote", id: docId }),
            fields: {
              documents(existing = [], { toReference }) {
                const newRef = toReference(quoteDocument);
                return newRef ? [newRef, ...existing] : existing;
              },
            },
          });
        },
      });
      const url = data?.generateQuotePdf.url;
      if (!url) throw new Error("Failed to generate PDF");
      return url;
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setLoadingPdf(null);
    }
  };

  // pdfLetterheadId is optional and trailing so that callers which compose with
  // no user choosing — sending a quote by email — stay unchanged and keep
  // resolving the letterhead server-side, as if nothing had been picked.
  const handleDownload = async (
    args: PdfDownloadArgs,
    pdfLetterheadId?: string | null
  ) => await generate(args, null, pdfLetterheadId);

  const handleSaveAndShare = async (
    args: PdfDownloadArgs,
    name: string,
    pdfLetterheadId?: string | null
  ) => await generate(args, { name }, pdfLetterheadId);

  return { handleDownload, handleSaveAndShare, loadingPdf };
}

function getLoadingKey(args: PdfDownloadArgs): string {
  switch (args.templateType) {
    case "none":
      return `${args.templateType}-${args.calculationType}`;
    case "contracting":
      return `${args.templateType}-${args.calculationType}-${args.contractingPdfId}`;
    case "sexy":
      return `${args.templateType}-${args.calculationType}-${args.pdfTemplateUrl}`;
    case "generator":
      return `${args.templateType}-${args.calculationType}-${args.pdfGeneratorTemplateId}`;
    default:
      assertNever(args);
  }
}

function toSourceInput(args: PdfDownloadArgs): QuoteDocumentSourceInput {
  switch (args.templateType) {
    case "none":
      return { source: "quote", calculationType: args.calculationType };
    case "contracting":
      return {
        source: "quote_contracting",
        calculationType: args.calculationType,
        contractingPdfId: args.contractingPdfId,
      };
    case "sexy":
      return { source: "quote_sexy", pdfTemplateUrl: args.pdfTemplateUrl };
    case "generator":
      return {
        source: "quote_generator",
        calculationType: args.calculationType,
        pdfGeneratorTemplateId: args.pdfGeneratorTemplateId,
      };
    default:
      assertNever(args);
  }
}
